import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import useProcurementContext from 'contexts/procurement.context/context';
import { ENUM_PROCUREMENT_STATUS, UpdateRejectedProcurementType } from 'models/procurement.model';
import FormStepper from 'components/base/form-stepper';
import { apiService } from 'services/base.service';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { toast } from 'react-toastify';
import UpdateRejectedProcurementDetails from './update-procurement-details';
import UpdateRejectedProcurementPreview from './update-procurement-preview';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const initialProcurement = {
  vendor_id: '',
  ingredient_id: '',
  id: '',
  vendor_ingredient_price: '',
  vendor_ingredient_unit_type: '',
  quantity: ''
};
const UpdateRejectedProcurement = () => {
  const [step, setStep] = useState(1);
  const [isUpdatingProcurementRequest, setIsUpdatingProcurementRequest] = useState(false);
  const { procurementToUpdate } = useProcurementContext();
  const [formData, setFormData] = useState<UpdateRejectedProcurementType[]>(
    procurementToUpdate.items.length > 0
      ? procurementToUpdate.items.map((vendor_ingredient) => {
          return {
            vendor_id: vendor_ingredient.vendor.id,
            ingredient_id: vendor_ingredient.ingredient_id,
            id: vendor_ingredient.id,
            vendor_name: vendor_ingredient.vendor.name,
            vendor_ingredient_price: vendor_ingredient.price,
            vendor_ingredient_unit_type: vendor_ingredient.ingredient.unit_type,
            quantity: vendor_ingredient.quantity
          };
        })
      : [initialProcurement]
  );
  const [vendorIngredientIdsToDelete, setVendorIngredientIdsToDelete] = useState<string[]>([]);
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.Procurement, RolePermission.Write);

  const setValue = (index: number, field: keyof UpdateRejectedProcurementType, value: string) => {
    setFormData((prev) =>
      prev.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    );
  };
  const addForm = () => setFormData([...formData, { ...initialProcurement }]);
  const removeItem = (index: number) => setFormData(formData.filter((data, i) => i !== index));
  const router = useNavigate();

  const optionalFields = ['id'];

  function isFormFilled(data: UpdateRejectedProcurementType[]): boolean {
    for (const item of data) {
      for (const key in item) {
        if (
          Object.prototype.hasOwnProperty.call(item, key) && // Safely check for the key
          typeof item[key as keyof UpdateRejectedProcurementType] === 'string' &&
          item[key as keyof UpdateRejectedProcurementType] === '' &&
          !optionalFields.includes(key) // Skip optional fields
        ) {
          return false;
        }
      }
    }
    return true;
  }

  const handleEdit = () => {
    router(`/main-store/procurement/update-details/${procurementToUpdate.code}`);
  };

  const data = {
    title: '.',
    note: '.',
    idsToDelete: vendorIngredientIdsToDelete,
    updated_items:
      formData
        .filter(
          (vendorIngredient) =>
            vendorIngredient.id && !vendorIngredientIdsToDelete.includes(vendorIngredient.id)
        )
        .map(({ vendor_id, ingredient_id, quantity, id }) => ({
          vendor_id,
          ingredient_id,
          quantity: Number(quantity),
          id
        })) ?? [],
    new_items: formData
      .filter((vendorIngredient) => !vendorIngredient.id)
      .map(({ vendor_id, ingredient_id, quantity }) => ({
        vendor_id,
        ingredient_id,
        quantity: Number(quantity)
      }))
  };

  const updateProcurementRequest = async () => {
    if (!canWriteProcurement) return;
    try {
      setIsUpdatingProcurementRequest(true);
      const response = await apiService.post(
        `main-store/procurements/${procurementToUpdate.id}/update`,
        { data }
      );
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.PROCUREMENT);
        toast(<ToastMessage message='Procurement request updated successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurementRequest(false);
    }
  };

  return (
    <RequestDetailsWrapper
      codeId={procurementToUpdate.code}
      statusTag={<ProcurementStatusTag type={ENUM_PROCUREMENT_STATUS.REJECTED} />}
      withCard={false}>
      <div className='max-w-[678px]'>
        <div className='bg-[#FFF4E5] py-2.5 px-4 w-max rounded mb-3'>
          <p className='text-[#FF9500] text-sm max-w-full'> {procurementToUpdate.note}</p>
        </div>
        <FormStepper
          disabled={step === 1 ? !isFormFilled(formData) : false}
          hidePrimaryAction={!canWriteProcurement}
          step={step}
          title={step === 1 ? 'Make your selections' : 'Review selections'}
          subtitle={step === 2 ? 'Ensure you have all items needed selected' : ''}
          setStep={setStep}
          handleConfirmClick={() => {
            updateProcurementRequest();
          }}
          hideSteps
          loading={isUpdatingProcurementRequest}
          handleCancelClick={() => {
            router(-1);
          }}>
          {step === 1 ? (
            <UpdateRejectedProcurementDetails
              formData={formData}
              setValue={setValue}
              addForm={addForm}
              removeItem={removeItem}
              canWrite={canWriteProcurement}
              setVendorIngredientIdsToDelete={setVendorIngredientIdsToDelete}
              vendorIngredientIdsToDelete={vendorIngredientIdsToDelete}
            />
          ) : (
            <UpdateRejectedProcurementPreview
              formData={formData.filter(
                (vendor_ingredient) => !vendorIngredientIdsToDelete.includes(vendor_ingredient.id)
              )}
              handleEdit={handleEdit}
            />
          )}
        </FormStepper>
      </div>
    </RequestDetailsWrapper>
  );
};

export default UpdateRejectedProcurement;
